import React, { Suspense } from 'react';

import { useBAPerfectOrderReport } from './baPerfectOrderQueries';

// Lazy-loaded so the heavy report is split out of the main bundle and only
// fetched when this partner page is actually opened.
const BAPerfectOrderReport = React.lazy(() => import('./BAPerfectOrderReport'));

const loadingMessage = <div style={{ padding: 24 }}>Loading Perfect Order Report…</div>;

/**
 * Authenticated wrapper for the Blue Apron Perfect Order Report.
 *
 * State is derived from React Query during render — no effects or mirror state.
 * The report is fed live rows via `initialData`. If the live feed fails we show
 * an explicit error rather than stale demo data: this report is shared with Blue
 * Apron, so silently rendering an old extract that looks live is worse than
 * surfacing the outage.
 */
export function BAPerfectOrderReportPage() {
    const { data: rows, error, isFetching, refetch, status } = useBAPerfectOrderReport();

    if (status === 'pending') {
        return loadingMessage;
    }

    if (status === 'error' && error?.forbidden) {
        return <div style={{ padding: 24 }}>You do not have access to the Perfect Order Report.</div>;
    }

    if (status === 'error') {
        return (
            <div style={{ padding: 24, maxWidth: 640 }}>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Perfect Order Report is unavailable</h1>
                <p style={{ margin: '8px 0 0', fontSize: 14, color: '#3A474E', lineHeight: 1.6 }}>
                    We couldn’t load live data for this report. This usually means the report service is temporarily
                    unavailable — please try again in a moment.
                </p>
                {error?.message ? (
                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#6B7A82' }}>Details: {error.message}</p>
                ) : null}
                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    style={{
                        marginTop: 16,
                        padding: '6px 14px',
                        fontSize: 13,
                        cursor: isFetching ? 'default' : 'pointer',
                        border: '1px solid #26343C',
                        background: isFetching ? '#6B7A82' : '#26343C',
                        color: '#FFFFFF',
                        borderRadius: 2,
                    }}
                >
                    {isFetching ? 'Retrying…' : 'Retry'}
                </button>
            </div>
        );
    }

    return (
        <Suspense fallback={loadingMessage}>
            <BAPerfectOrderReport initialData={rows} />
        </Suspense>
    );
}
